/**
 * Collapsed inline proposal card for the chat flow.
 *
 * Displays a compact, clickable summary of a proposal including:
 * - Proposal title
 * - Resolution badge (color-coded)
 * - Action type icons sourced from ACTION_REGISTRY
 * - Inline Approve / Reject buttons (pending proposals only)
 * - "Open in panel" button
 *
 * Data is fetched lazily via IntersectionObserver so off-screen cards
 * don't incur network cost.
 */

import { useCallback, useState } from 'react';

import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { logger } from '@/lib/logger';
import type { ProposedAction } from '@/lib/proposals/core/types';
import { useProposalOptional } from '@/lib/proposals/hooks/useProposal';
import { useProposalDecision } from '@/lib/proposals/hooks/useProposalActions';
import { useProposalVisibility } from '@/lib/proposals/hooks/useProposalVisibility';
import { lookupAction } from '@/lib/proposals/registry/utils';
import {
  badgeColorForResolution,
  getResolutionStyles,
  isSettledResolution,
} from '@/lib/proposals/utils/resolutionStyles';
import { cx } from '@/utils/cx';

import { ResolutionMark } from './ResolutionMark';
import { useStageMoveSummary } from './useStageMoveSummary';

// ============================================================================
// Unique icon deduplication
// ============================================================================

/**
 * Extract unique action type icons from a list of actions.
 * Deduplicates by registry category so we don't show the same icon twice.
 */
function getUniqueActionIcons(
  actions: ProposedAction[]
): Array<{ key: string; Icon: React.ComponentType<{ className?: string }> }> {
  const seen = new Set<string>();
  const icons: Array<{
    key: string;
    Icon: React.ComponentType<{ className?: string }>;
  }> = [];

  for (const action of actions) {
    const entry = lookupAction(action);
    if (!entry) {
      continue;
    }
    const dedupKey = entry.category;
    if (!seen.has(dedupKey)) {
      seen.add(dedupKey);
      icons.push({ key: `${dedupKey}-${action.id}`, Icon: entry.icon });
    }
  }

  return icons;
}

// ============================================================================
// "Open in panel" SVG icon
// ============================================================================

function OpenPanelIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      {/* Side panel open icon — vertical split with arrow pointing right */}
      <rect x='3' y='3' width='18' height='18' rx='2' />
      <line x1='15' y1='3' x2='15' y2='21' />
      <path d='M9 9l3 3-3 3' />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export interface InlineProposalCardProps {
  /** Proposal ID (used for data fetch and sidebar open) */
  proposalId: number;
  /** Organization ID */
  orgId: number;
  /** Display title (shown immediately, no loading needed) */
  title: string;
  /** Current resolution state */
  resolution: string;
  /** Whether the card is in expanded state (controls aria-expanded) */
  isExpanded: boolean;
  /** Called when the card header area is clicked */
  onToggleExpand: () => void;
  /** When true, applies a subtle highlight to indicate a freshly-created proposal */
  isFresh?: boolean;
  /**
   * True inside a builder conversation. Suppresses the inline Approve/Reject
   * controls — Save in the builder panel is the only apply path there.
   */
  isBuilderMode?: boolean;
  /** Whether this is the proposal currently driving the builder panel. */
  isSelected?: boolean;
  /** Selects this proposal, called alongside `onToggleExpand` on header activation. */
  onSelect?: () => void;
}

/**
 * Collapsed inline proposal card rendered inside chat messages.
 *
 * - Lazily fetches full proposal data when near the viewport
 * - Shows action-type icons from ACTION_REGISTRY once data is loaded
 * - Shows Approve / Reject inline for pending proposals
 * - "Open in panel" opens the ProposalSidebar
 */
export function InlineProposalCard({
  proposalId,
  orgId,
  title,
  resolution,
  isExpanded,
  onToggleExpand,
  isFresh = false,
  isBuilderMode = false,
  isSelected = false,
  onSelect,
}: InlineProposalCardProps) {
  const { isVisible, sentinelRef } = useProposalVisibility();
  const { openProposal, notifyProposalUpdated } = useProposalSidebar();
  const { approve, reject, isApproving, isRejecting } =
    useProposalDecision(orgId);
  const [error, setError] = useState<string | null>(null);

  // Only fetch full data when the card is near the viewport
  const { data: proposal } = useProposalOptional(
    orgId,
    isVisible ? proposalId : undefined
  );

  const resolutionStyles = getResolutionStyles(resolution);
  const isPending = resolution === 'pending';
  // Settled = history. Pending and executing are live states and keep the
  // pill; everything else reads as an event that already happened.
  const isSettled = isSettledResolution(resolution);
  // A single stage move names its target in the primary button and reads in
  // the past tense once approved. The row itself keeps the generic action-type
  // icons: a collapsed row has no space for a current → proposed diff, and the
  // expanded card renders the real one against live data.
  const stageMove = useStageMoveSummary(proposal?.actions, orgId);
  const approveLabel = stageMove?.proposedLabel
    ? `Move to ${stageMove.proposedLabel}`
    : 'Approve';
  // Once a stage move has happened the row is a record of it, so it reads
  // in the past tense — "Moved Acme" + the chips — rather than repeating a
  // proposal title that was written to ask a question. The check mark then
  // carries the outcome and the word "Approved" would only say it twice.
  const isMoveRecord =
    resolution === 'approved' && Boolean(stageMove?.objectLabel);
  const headline = isMoveRecord ? `Moved ${stageMove?.objectLabel}` : title;

  const handleHeaderActivate = useCallback(() => {
    onToggleExpand();
    onSelect?.();
  }, [onToggleExpand, onSelect]);

  const handleOpenInPanel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openProposal(proposalId);
    },
    [openProposal, proposalId]
  );

  const handleApprove = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setError(null);
      try {
        await approve(proposalId);
        notifyProposalUpdated();
      } catch (err) {
        logger.error(
          'Failed to approve proposal',
          err instanceof Error ? err : undefined,
          {
            component: 'actions',
            proposal_id: proposalId,
            error_type: err instanceof Error ? err.constructor.name : 'unknown',
          }
        );
        setError("We couldn't approve this. Try again.");
      }
    },
    [approve, proposalId, notifyProposalUpdated]
  );

  const handleReject = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setError(null);
      try {
        await reject(proposalId);
        notifyProposalUpdated();
      } catch (err) {
        logger.error(
          'Failed to reject proposal',
          err instanceof Error ? err : undefined,
          {
            component: 'actions',
            proposal_id: proposalId,
            error_type: err instanceof Error ? err.constructor.name : 'unknown',
          }
        );
        setError("We couldn't reject this. Try again.");
      }
    },
    [reject, proposalId, notifyProposalUpdated]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHeaderActivate();
      }
    },
    [handleHeaderActivate]
  );

  const actionIcons = proposal ? getUniqueActionIcons(proposal.actions) : [];
  const isDeciding = isApproving || isRejecting;
  let headerLabel: string;
  if (isBuilderMode) {
    headerLabel = `Show "${title}" in the builder panel`;
  } else if (isExpanded) {
    headerLabel = 'Collapse action';
  } else {
    headerLabel = 'Expand action';
  }

  return (
    <div
      ref={sentinelRef}
      className={cx(
        'border rounded-xl motion-safe:transition-all',
        // Never wider than the chat column. `min-w-0` lets the card shrink
        // below its content's intrinsic width (a flex/grid child defaults to
        // min-width:auto and refuses to), which is what lets the title
        // truncate instead of pushing the whole conversation sideways on a
        // phone.
        'w-full min-w-0 max-w-full',
        // bg-bg-primary: top-level chat module cards share the shell canon
        // (VARIANT_SHELL) — bg-bg-secondary is reserved for nested inner cards.
        'bg-bg-primary border-border-secondary',
        isSelected
          ? 'ring-2 ring-utility-brand-600'
          : isFresh && 'ring-2 ring-utility-brand-200'
      )}
    >
      {/* Main clickable header row */}
      <div
        role='button'
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-pressed={isBuilderMode ? isSelected : undefined}
        aria-label={headerLabel}
        onClick={handleHeaderActivate}
        onKeyDown={handleKeyDown}
        className='flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 cursor-pointer hover:bg-bg-tertiary rounded-xl motion-safe:transition-colors min-h-[44px]'
      >
        {/* Status. A live proposal (pending / executing) wears a pill
            because there is still something to do; a settled one gets a
            mark, because it is a record. "Applied automatically" is
            provenance, not a status, so it lives in the quiet meta text
            beside the delta instead of a pill of its own. */}
        {isSettled ? (
          <ResolutionMark resolution={resolution} iconOnly={isMoveRecord} />
        ) : (
          <Badge
            type='pill-color'
            color={badgeColorForResolution(resolution)}
            size='sm'
          >
            {resolutionStyles.label}
          </Badge>
        )}

        {/* Headline. `min-w-0` is what makes `truncate` work at all here —
            without it this flex child keeps its intrinsic width and a long
            proposal name widens the row past the viewport. `flex-1` with
            `max-w-max` sizes it to its own text but no wider, so it is the
            item that yields (truncates) when the row is tight, while the
            chips that follow stay whole and read straight on from it. */}
        <span className='min-w-[12ch] flex-1 max-w-max text-sm font-medium text-fg-primary truncate'>
          {headline}
        </span>

        {/* Selection indicator — "showing in panel" in both hosts: the
            builder's record panel and the ordinary chat's docked pane. */}
        {isSelected && (
          <Badge type='pill-color' color='brand' size='sm'>
            Showing in panel
          </Badge>
        )}

        {/* Action type icons (shown once proposal data is loaded).
            Not in builder mode: one glyph per action category is a lossy
            summary of a change the panel beside the chat is already rendering
            in full, with real diffs. Here the card only has to name the
            proposal so the user can pick which one the panel shows. */}
        {!isBuilderMode && actionIcons.length > 0 && (
          <span className='flex items-center gap-1 shrink-0'>
            {actionIcons.map(({ key, Icon }) => (
              <span key={key} className='text-fg-tertiary' aria-hidden='true'>
                <Icon className='w-4 h-4' />
              </span>
            ))}
          </span>
        )}

        {/* Open in panel button. Not in builder mode: the whole card already
            IS the open-the-panel control there, so a second button inside it
            gives one action two hit targets — and the outer one swallows the
            inner one's click on a phone. */}
        {!isBuilderMode && (
          <Button
            color='tertiary'
            size='sm'
            aria-label={`Open "${title}" in side panel`}
            onClick={handleOpenInPanel}
            iconLeading={OpenPanelIcon}
            className='ml-auto shrink-0 -mr-2 sm:order-last'
          />
        )}
      </div>

      {/* Inline Approve / Reject for pending proposals. Suppressed in builder
          mode, where Save in the panel is the only apply path — a second
          approve/reject control here would compete with it. */}
      {isPending && !isBuilderMode && (
        <>
          {/* NOTE — a deliberate visual change: Approve was hand-rolled in
              `utility-success` green. `Button` has no success colour, and the
              doc's instruction when a variant is missing is to EXTEND the
              library, not fork it — so rather than reintroduce a bespoke green
              here, Approve is the primary action and Reject the secondary,
              which is what the library already expresses. If green is
              load-bearing for this decision, the fix is a `success` colour on
              Button, applied everywhere, not one more local override. */}
          <div className='flex items-center gap-2 px-4 pb-3'>
            <Button
              color='primary'
              size='md'
              aria-label={`Approve action: ${title}`}
              onClick={handleApprove}
              isDisabled={isDeciding}
              isLoading={isApproving}
              className='flex-1'
            >
              {isApproving ? 'Approving…' : approveLabel}
            </Button>
            <Button
              color='secondary'
              size='md'
              aria-label={`Reject action: ${title}`}
              onClick={handleReject}
              isDisabled={isDeciding}
              isLoading={isRejecting}
              className='flex-1'
            >
              {isRejecting ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
          {error && (
            <p
              role='alert'
              className='px-4 pb-2 text-xs text-utility-error-700'
            >
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
